import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useOrganization } from './OrganizationContext';
import { devLog } from '../utils/devLogger';
import { cleanOrgName } from '../utils/formatters';
import { BACKEND_URL } from '../config';

/**
 * Command Context - SINGLE SOURCE OF TRUTH
 * 
 * All Command-related agent data comes exclusively from /api/acc/agents
 * DO NOT use /api/agent or any other endpoint for Command agents
 * 
 * This context:
 * - Fetches agents from /api/acc/agents (ALWAYS scoped to active organization)
 * - Provides agent count for badges (sidebar, workspace, etc.)
 * - Manages Command drawer visibility
 * - Handles global keyboard shortcut (Cmd+K)
 * 
 * P0 FIX: Organisation Scoping
 * - Agents are ALWAYS filtered by the currently selected organization
 * - Agent list resets when organization changes
 * - Badge count reflects ONLY agents the user can run in the active org
 */
const CommandContext = createContext(null);

export const CommandProvider = ({ children }) => {
  // Get active organization for API requests (may be undefined during initial load)
  const orgContext = useOrganization();
  const activeOrganization = orgContext?.activeOrganization;
  
  const [isOpen, setIsOpen] = useState(false);
  const [agents, setAgents] = useState([]);
  const [agentCount, setAgentCount] = useState(0);
  const [workers, setWorkers] = useState([]);
  const [workerCount, setWorkerCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  
  // Workflow context — set when user is editing a workflow in the builder
  const [workflowContext, setWorkflowContext] = useState(null);
  
  // P0 FIX: Track the org_id that agents were fetched for
  const [fetchedForOrgId, setFetchedForOrgId] = useState(null);
  
  // P0 FIX: "No organization" state message
  const [noOrgMessage, setNoOrgMessage] = useState(null);
  
  // B1 FINAL: Agent to run - set by Command, consumed by pages with RunAgentModal
  const [agentToRun, setAgentToRun] = useState(null);
  
  // Phase 4 B2: Agent being edited via Command — set by Builder, consumed by Command
  const [editingAgent, setEditingAgent] = useState(null);
  
  // Signal that increments when Command successfully modifies an agent — Builder listens to re-fetch
  const [agentModifiedSignal, setAgentModifiedSignal] = useState(0);
  const notifyAgentModified = useCallback(() => {
    setAgentModifiedSignal(prev => prev + 1);
  }, []);
  
  const openCommand = useCallback(() => {
    devLog.command('Opening Command drawer');
    setIsOpen(true);
  }, []);
  const closeCommand = useCallback(() => {
    devLog.command('Closing Command drawer');
    setIsOpen(false);
  }, []);
  const toggleCommand = useCallback(() => {
    setIsOpen(prev => {
      devLog.command(prev ? 'Toggling Command closed' : 'Toggling Command open');
      return !prev;
    });
  }, []);
  
  // Phase 4 B2: Open Command in editing mode for a specific agent
  const openCommandForAgent = useCallback((agent) => {
    setEditingAgent({
      agent_id: agent.agent_id || agent.id,
      agent_name: agent.name,
      workflow_state: agent.workflow_state || null,
      // Source collection — detects which modify route to use:
      //   "agent"      → /api/command/modify-agent (writes agents.flow.nodes)
      //   "automation" → /api/command/builder-step  (writes automations.steps[])
      source: agent.source || agent._source || (agent.agent_id?.startsWith?.('auto_') || agent.id?.startsWith?.('auto_') ? 'automation' : 'agent'),
      origin: 'builder',
    });
    setIsOpen(true);
  }, []);
  
  // Phase 4 B2: Clear editing context
  const clearEditingAgent = useCallback(() => {
    setEditingAgent(null);
  }, []);
  
  // B1 FINAL: Request to run an agent - closes Command and signals pages
  const requestRunAgent = useCallback((agent) => {
    setAgentToRun(agent);
    setIsOpen(false); // Close Command drawer
  }, []);
  
  // B1 FINAL: Clear the agent to run (called by consuming page after opening modal)
  const clearAgentToRun = useCallback(() => {
    setAgentToRun(null);
  }, []);
  
  /**
   * SINGLE SOURCE OF TRUTH: Fetch agents from /api/acc/agents
   * This is the ONLY place that should fetch Command agents
   * 
   * P0 FIX: Always passes organization_id, clears state on org change
   */
  const fetchAgents = useCallback(async (force = false) => {
    // Skip if recently fetched (within 30 seconds) unless forced
    // P0 FIX: Also check if org_id matches what we already fetched
    const currentOrgId = activeOrganization?.organization_id;
    
    if (!force && lastFetchTime && Date.now() - lastFetchTime < 30000 && fetchedForOrgId === currentOrgId) {
      devLog.agent('Skipping fetch - cached data still valid', { org_id: currentOrgId });
      return;
    }
    
    // Don't fetch if no organization is selected
    if (!currentOrgId) {
      devLog.org('No active organization - showing message');
      setNoOrgMessage('Select an organisation to use Command.');
      setAgents([]);
      setAgentCount(0);
      setFetchedForOrgId(null);
      return;
    }
    
    // Clear "no org" message
    setNoOrgMessage(null);
    
    try {
      setLoading(true);
      setError(null);
      
      // P0 FIX: If org changed, immediately clear stale agents to prevent miscount
      if (fetchedForOrgId && fetchedForOrgId !== currentOrgId) {
        console.debug('[Command] Org changed, clearing stale agents. Old:', fetchedForOrgId, 'New:', currentOrgId);
        setAgents([]);
        setAgentCount(0);
      }
      
      // Build headers with organization context
      const headers = {
        'Content-Type': 'application/json',
        'X-Organization-Id': currentOrgId
      };
      
      console.debug('[Command] Fetching agents for org:', currentOrgId, activeOrganization?.name);
      
      const response = await fetch(`${BACKEND_URL}/api/acc/agents`, {
        credentials: 'include',
        headers
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          // Not authenticated - clear agents
          setAgents([]);
          setAgentCount(0);
          setFetchedForOrgId(null);
          return;
        }
        throw new Error('Failed to fetch agents');
      }
      
      const data = await response.json();
      const fetchedAgents = data.agents || [];
      
      // Debug logging for agent count verification
      console.debug('[ACC_AGENTS_COUNT]', { 
        org_id: currentOrgId,
        count: fetchedAgents.length, 
        ids: fetchedAgents.map(a => a.agent_id || a.id),
        meta: data.meta 
      });
      
      // P0 FIX: Track which org these agents belong to
      setFetchedForOrgId(currentOrgId);
      
      // HARD ENFORCEMENT: Only set agents from this response
      setAgents(fetchedAgents);
      setAgentCount(fetchedAgents.length);
      setLastFetchTime(Date.now());
      
      // Also fetch Service OS workers for the unified view
      try {
        const workersResp = await fetch(`${BACKEND_URL}/api/service-os/automations`, {
          credentials: 'include',
          headers
        });
        if (workersResp.ok) {
          const workersData = await workersResp.json();
          const fetchedWorkers = workersData.automations || [];
          setWorkers(fetchedWorkers);
          setWorkerCount(fetchedWorkers.length);
        }
      } catch (wErr) {
        console.debug('[Command] Workers fetch failed (non-critical):', wErr.message);
      }
      
    } catch (err) {
      console.error('[Command] Error fetching agents:', err);
      setError(err.message);
      // On error, don't show stale data
      setAgents([]);
      setAgentCount(0);
      setFetchedForOrgId(null);
    } finally {
      setLoading(false);
    }
  }, [lastFetchTime, activeOrganization?.organization_id, activeOrganization?.name, fetchedForOrgId]);
  
  // Fetch agents when Command opens (if not recently fetched)
  useEffect(() => {
    if (isOpen && activeOrganization?.organization_id) {
      fetchAgents();
    }
  }, [isOpen, activeOrganization?.organization_id, fetchAgents]);
  
  // P0 FIX: Refetch when organization changes - use a ref to track previous org
  // This is CRITICAL for correct org scoping
  // P1 FIX: Only refresh on ACTUAL org switch (from one valid org to another), 
  // NOT on initial load (undefined -> valid). This prevents unnecessary remounts.
  const prevOrgRef = useRef(activeOrganization?.organization_id);
  const isInitialLoadRef = useRef(true);
  
  useEffect(() => {
    const currentOrgId = activeOrganization?.organization_id;
    
    // Clear "no org" message if org is selected
    if (currentOrgId) {
      setNoOrgMessage(null);
    } else {
      setNoOrgMessage('Select an organisation to use Command.');
      setAgents([]);
      setAgentCount(0);
    }
    
    // P1 FIX: Only trigger refresh on ACTUAL org switch, not initial load
    // Initial load is when prevOrgRef.current is undefined and org becomes valid
    if (currentOrgId && currentOrgId !== prevOrgRef.current) {
      const isActualOrgSwitch = prevOrgRef.current !== undefined;
      
      if (isActualOrgSwitch) {
        // This is a real org switch (user changed organization)
        console.debug('[Command] Organization switched from', prevOrgRef.current, 'to', currentOrgId, '- refreshing agents');
        // Immediately clear stale data and fetch fresh
        setAgents([]);
        setAgentCount(0);
        fetchAgents(true); // Force refresh on actual org switch
      } else if (isInitialLoadRef.current) {
        // This is initial load - just update ref without forcing refresh
        console.debug('[Command] Initial organization load:', currentOrgId);
        isInitialLoadRef.current = false;
      }
      
      prevOrgRef.current = currentOrgId;
    }
  }, [activeOrganization?.organization_id, fetchAgents]);
  
  // Fetch agents on initial mount for badge count display
  // This ensures the badge shows correct count before user opens Command
  useEffect(() => {
    // Wait for organization to be available
    if (!activeOrganization?.organization_id) {
      return;
    }
    // Small delay to ensure auth is ready
    const timer = setTimeout(() => {
      fetchAgents();
    }, 500);
    return () => clearTimeout(timer);
  }, [activeOrganization?.organization_id, fetchAgents]);
  
  // P1: Auto-open Command on startup if user preference is set
  // Uses sessionStorage to ensure we only auto-open once per browser session
  // Retries if the first fetch fails (e.g. auth cookies not yet established after login)
  useEffect(() => {
    if (!activeOrganization?.organization_id) return;
    if (sessionStorage.getItem('command_auto_opened')) return;
    
    let cancelled = false;
    const attemptAutoOpen = async (attempt = 1) => {
      if (cancelled) return;
      try {
        const resp = await fetch(`${BACKEND_URL}/api/account/preferences`, { credentials: 'include' });
        if (cancelled) return;
        if (resp.status === 401 && attempt < 4) {
          // Auth not ready yet — retry with backoff
          setTimeout(() => attemptAutoOpen(attempt + 1), attempt * 800);
          return;
        }
        if (!resp.ok) return;
        const prefs = await resp.json();
        if (prefs.command_default_view && !cancelled) {
          sessionStorage.setItem('command_auto_opened', '1');
          setTimeout(() => { if (!cancelled) setIsOpen(true); }, 400);
        }
      } catch (e) {
        // Network error — retry once
        if (attempt < 3 && !cancelled) {
          setTimeout(() => attemptAutoOpen(attempt + 1), attempt * 800);
        }
      }
    };
    
    // Initial delay to let layout settle after login/navigation
    const timer = setTimeout(() => attemptAutoOpen(1), 500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [activeOrganization?.organization_id]);
  
  // Global keyboard shortcut for Command (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        e.stopPropagation();
        toggleCommand();
      }
    };
    
    // ESC key to close Command
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        closeCommand();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('keydown', handleEsc);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keydown', handleEsc);
    };
  }, [toggleCommand, closeCommand, isOpen]);
  
  // Listen for custom events to open Command
  useEffect(() => {
    const handleOpenCommand = () => openCommand();
    window.addEventListener('open-command', handleOpenCommand);
    return () => window.removeEventListener('open-command', handleOpenCommand);
  }, [openCommand]);
  
  // Listen for auth changes to refresh agents
  useEffect(() => {
    const handleAuthChange = () => {
      fetchAgents(true); // Force refresh on auth change
    };
    window.addEventListener('auth-change', handleAuthChange);
    return () => window.removeEventListener('auth-change', handleAuthChange);
  }, [fetchAgents]);
  
  return (
    <CommandContext.Provider value={{
      // Drawer state
      isOpen,
      openCommand,
      closeCommand,
      toggleCommand,
      
      // Agent data - SINGLE SOURCE OF TRUTH
      agents,
      agentCount,
      loading,
      error,
      
      // Service OS Workers
      workers,
      workerCount,
      
      // Workflow editing context
      workflowContext,
      setWorkflowContext,
      
      // P0 FIX: Organization context for Command
      activeOrganizationId: activeOrganization?.organization_id,
      activeOrganizationName: cleanOrgName(activeOrganization?.name),
      noOrgMessage,
      
      // Force refresh
      refreshAgents: () => fetchAgents(true),
      
      // B1 FINAL: Agent run request from Command
      agentToRun,
      requestRunAgent,
      clearAgentToRun,
      
      // Phase 4 B2: Agent editing via Command
      editingAgent,
      setEditingAgent,
      openCommandForAgent,
      clearEditingAgent,
      agentModifiedSignal,
      notifyAgentModified,
    }}>
      {children}
    </CommandContext.Provider>
  );
};

export const useCommand = () => {
  const context = useContext(CommandContext);
  if (!context) {
    throw new Error('useCommand must be used within a CommandProvider');
  }
  return context;
};

// Helper function to open Command from anywhere (even outside React)
export const openCommandGlobal = () => {
  window.dispatchEvent(new CustomEvent('open-command'));
};

export default CommandContext;
